import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Library,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import useSWR from 'swr';

import { AppShell, Breadcrumbs } from '@/components/landing/AppShell';
import { InlineChapterTitleEdit } from '@/components/lesson-packs/InlineChapterTitleEdit';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import type {
  CreateCurriculumChapterDto,
  CurriculumChapter,
  CurriculumTopic,
} from '@/lib/schema.types';
import { cn } from '@/lib/utils';

/**
 * Admin page for the curriculum catalogue. Three-level filter at the top
 * (board / class / subject) drives a table of chapters sorted by
 * `displayOrder`. Inline-edit `chapterTitle` / `chapterNumber` /
 * `displayOrder`; expand a row to edit its `topics[]`; add chapters with
 * the "+ Add chapter" button; delete with confirm.
 *
 * Why this page exists: ~40 catalogue chapters were extracted from PDFs
 * with body-text titles (Class 6 Maths "Reshma lives in a coastal
 * town…", Class 10 English "BEFORE YOU READ", etc. — see
 * CURRICULUM_API.md §5). Admins clean these up here rather than hitting
 * the API directly.
 *
 * All mutations: PATCH only sends the fields you changed (per §3 partial-
 * update rule). Sending `topics` REPLACES the whole array — so the topic
 * editor builds the new array locally then PATCHes once on save.
 */
export default function CurriculumAdminPage() {
  // Top-of-page filter cascade. Defaults: CBSE / Class 6 / nothing.
  const [board, setBoard] = useState('CBSE');
  const [classNum, setClassNum] = useState<number | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);

  // SWR fetches (same caching guidance as the picker — see
  // CURRICULUM_API.md §6).
  const boardsSWR = useSWR(['/curriculum/boards'], () =>
    api.curriculum.listBoards(),
  );
  const classesSWR = useSWR(
    board ? ['/curriculum/classes', board] : null,
    () => api.curriculum.listClasses({ board }),
  );
  const subjectsSWR = useSWR(
    board && classNum
      ? ['/curriculum/subjects', board, classNum]
      : null,
    () => api.curriculum.listSubjects({ board, class: classNum! }),
  );
  const chaptersSWR = useSWR(
    board && classNum && subject
      ? ['/curriculum/chapters', board, classNum, subject]
      : null,
    () =>
      api.curriculum.listChapters({
        board,
        class: classNum!,
        subject,
      }),
  );

  const chapters = useMemo(() => {
    const list = chaptersSWR.data ?? [];
    return [...list].sort(
      (a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999),
    );
  }, [chaptersSWR.data]);

  const refetchChapters = () => void chaptersSWR.mutate();

  return (
    <AppShell activeNav="curriculum">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col px-8 pt-6 pb-16">
        <Breadcrumbs
          items={[{ label: 'Curriculum' }]}
        />

        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-semibold tracking-tight text-fg">
              Curriculum
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Browse and edit chapters and topics for each board / class /
              subject.
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            className="px-5"
            disabled={!board || !classNum || !subject}
            onClick={() => setAddOpen(true)}
          >
            <Plus size={15} /> Add chapter
          </Button>
        </div>

        {/* Filter bar */}
        <div className="mt-6 grid grid-cols-1 gap-3 rounded-xl border border-border bg-bg-panel p-4 sm:grid-cols-3">
          <SelectField
            label="Board"
            value={board}
            options={(boardsSWR.data ?? []).map((b) => ({ value: b, label: b }))}
            onChange={(v) => {
              setBoard(v);
              setClassNum(null);
              setSubject('');
            }}
            loading={boardsSWR.isLoading}
          />
          <SelectField
            label="Class"
            value={classNum ? String(classNum) : ''}
            options={(classesSWR.data ?? []).map((n) => ({
              value: String(n),
              label: `Class ${n}`,
            }))}
            onChange={(v) => {
              setClassNum(v ? Number(v) : null);
              setSubject('');
            }}
            disabled={!board}
            loading={classesSWR.isLoading}
            placeholder="Pick a class"
          />
          <SelectField
            label="Subject"
            value={subject}
            options={(subjectsSWR.data ?? []).map((s) => ({ value: s, label: s }))}
            onChange={setSubject}
            disabled={!classNum}
            loading={subjectsSWR.isLoading}
            placeholder="Pick a subject"
          />
        </div>

        {/* Body */}
        {!subject ? (
          <EmptyState
            title="Pick a board, class, and subject to see chapters"
            detail="The cascade above filters the catalogue. All catalogues are partial — adding chapters here will surface them in the Generate Lesson Pack picker too."
          />
        ) : chaptersSWR.isLoading ? (
          <div className="mt-12 flex justify-center text-fg-muted">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : chapters.length === 0 ? (
          <EmptyState
            title={`No chapters in catalogue for Class ${classNum} ${subject}`}
            detail="Use 'Add chapter' to seed this slice manually, or run the backend's PDF ingest pipeline."
          />
        ) : (
          <ChaptersTable chapters={chapters} onChanged={refetchChapters} />
        )}
      </div>

      {addOpen && board && classNum && subject && (
        <AddChapterDialog
          open
          onClose={() => setAddOpen(false)}
          board={board}
          classNum={classNum}
          subject={subject}
          existingDisplayOrders={chapters.map((c) => c.displayOrder ?? 0)}
          onCreated={() => {
            setAddOpen(false);
            refetchChapters();
          }}
        />
      )}
    </AppShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Chapters table                                                              */
/* -------------------------------------------------------------------------- */

function ChaptersTable({
  chapters,
  onChanged,
}: {
  chapters: CurriculumChapter[];
  onChanged: () => void;
}) {
  return (
    <div className="mt-6 overflow-hidden rounded-xl border border-border bg-bg-panel">
      <table className="w-full text-left text-[13px]">
        <thead className="border-b border-border-subtle bg-bg-subtle/40 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
          <tr>
            <th className="w-8 px-2 py-2" />
            <th className="w-16 px-2 py-2">Order</th>
            <th className="w-20 px-2 py-2">Number</th>
            <th className="px-2 py-2">Title</th>
            <th className="w-20 px-2 py-2">Topics</th>
            <th className="w-12 px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {chapters.map((ch) => (
            <ChapterRow key={ch._id} chapter={ch} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChapterRow({
  chapter,
  onChanged,
}: {
  chapter: CurriculumChapter;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDelete = async () => {
    if (!confirm(`Delete "${chapter.chapterTitle}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await api.curriculum.deleteChapter(chapter._id);
      onChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <tr className="border-b border-border-subtle hover:bg-bg-subtle/30">
        <td className="px-2 py-2 align-top">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="grid h-6 w-6 place-items-center rounded text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
            title={expanded ? 'Collapse' : 'Expand topics'}
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </td>
        <td className="px-2 py-2 align-top font-mono text-[11.5px] text-fg-muted">
          <InlineNumberPatch
            chapterId={chapter._id}
            field="displayOrder"
            value={chapter.displayOrder ?? 0}
            onSaved={onChanged}
          />
        </td>
        <td className="px-2 py-2 align-top font-mono text-[11.5px] text-fg-muted">
          <InlineStringPatch
            chapterId={chapter._id}
            field="chapterNumber"
            value={chapter.chapterNumber ?? ''}
            onSaved={onChanged}
          />
        </td>
        <td className="group px-2 py-2 align-top">
          <div className="flex items-center gap-2">
            <span className="flex-1 text-fg">{chapter.chapterTitle}</span>
            <InlineChapterTitleEdit
              chapterId={chapter._id}
              currentTitle={chapter.chapterTitle}
              onSaved={onChanged}
            />
          </div>
        </td>
        <td className="px-2 py-2 align-top text-[11.5px] text-fg-muted">
          {chapter.topics.length}
        </td>
        <td className="px-2 py-2 align-top">
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="grid h-7 w-7 place-items-center rounded-md text-fg-muted transition hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
            title="Delete chapter"
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border-subtle bg-bg-subtle/20">
          <td colSpan={6} className="px-6 py-4">
            <TopicsEditor chapter={chapter} onSaved={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Topics editor (replaces the whole `topics[]` array on save per §3)          */
/* -------------------------------------------------------------------------- */

function TopicsEditor({
  chapter,
  onSaved,
}: {
  chapter: CurriculumChapter;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<CurriculumTopic[]>(chapter.topics);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(chapter.topics),
    [draft, chapter.topics],
  );

  const setTopic = (i: number, patch: Partial<CurriculumTopic>) =>
    setDraft((prev) => prev.map((t, ix) => (ix === i ? { ...t, ...patch } : t)));
  const removeTopic = (i: number) =>
    setDraft((prev) => prev.filter((_, ix) => ix !== i));
  const addTopic = () =>
    setDraft((prev) => [
      ...prev,
      { number: '', title: 'New topic', pageStart: null, pageEnd: null, content: null },
    ]);

  const save = async () => {
    // PATCH partial-update rule: only `topics` is sent. The backend
    // replaces the whole array — that's intentional and documented (§3).
    setSaving(true);
    setError(null);
    try {
      await api.curriculum.updateChapter(chapter._id, { topics: draft });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
          Topics ({draft.length})
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={addTopic} disabled={saving}>
            <Plus size={11} /> Add topic
          </Button>
          {dirty && (
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={11} className="animate-spin" /> : null}
              Save topics
            </Button>
          )}
        </div>
      </div>

      {draft.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-white px-3 py-3 text-[12px] text-fg-subtle">
          No topics for this chapter. Generate Lesson Pack will fall back to
          free-text topic input.
        </p>
      ) : (
        <div className="space-y-1.5">
          {draft.map((t, i) => (
            <div
              key={i}
              className="rounded border border-border bg-white px-2 py-1.5"
            >
              <div className="flex items-center gap-1.5">
                <input
                  value={t.number ?? ''}
                  onChange={(e) => setTopic(i, { number: e.target.value })}
                  placeholder="5.1"
                  className="h-7 w-16 rounded border border-border-subtle bg-white px-1.5 text-[11.5px] text-fg-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
                />
                <input
                  value={t.title}
                  onChange={(e) => setTopic(i, { title: e.target.value })}
                  placeholder="Topic title"
                  className="h-7 flex-1 rounded border border-border-subtle bg-white px-1.5 text-[12px] text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
                />
                <button
                  type="button"
                  onClick={() => removeTopic(i)}
                  className="grid h-6 w-6 place-items-center rounded text-fg-subtle transition hover:bg-red-50 hover:text-red-500"
                  title="Remove topic"
                >
                  <Trash2 size={11} />
                </button>
              </div>
              {/* Reference content — fed to the lesson-pack prompt as referenceText. */}
              <textarea
                value={t.content ?? ''}
                onChange={(e) => setTopic(i, { content: e.target.value || null })}
                placeholder="Reference content for this topic (optional) — auto-used as the lesson-pack reference text"
                rows={2}
                className="mt-1.5 w-full resize-y rounded border border-border-subtle bg-white px-1.5 py-1 text-[11.5px] text-fg-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
              />
              {(t.content?.trim()?.length ?? 0) > 0 && (
                <span className="mt-0.5 block text-[10px] text-fg-subtle">
                  {t.content!.trim().length.toLocaleString()} chars
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-[11.5px] text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tiny inline PATCH helpers for displayOrder + chapterNumber                  */
/* -------------------------------------------------------------------------- */

function InlineStringPatch({
  chapterId,
  field,
  value,
  onSaved,
}: {
  chapterId: string;
  field: 'chapterNumber';
  value: string;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const commit = async () => {
    if (draft === value) return;
    try {
      await api.curriculum.updateChapter(chapterId, { [field]: draft });
      onSaved();
    } catch {
      setDraft(value);
    }
  };
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="h-6 w-full rounded border border-transparent bg-transparent px-1 text-[11.5px] text-fg-muted focus:border-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-accent/20"
    />
  );
}

function InlineNumberPatch({
  chapterId,
  field,
  value,
  onSaved,
}: {
  chapterId: string;
  field: 'displayOrder';
  value: number;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const commit = async () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n === value) {
      setDraft(String(value));
      return;
    }
    try {
      await api.curriculum.updateChapter(chapterId, { [field]: n });
      onSaved();
    } catch {
      setDraft(String(value));
    }
  };
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(String(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      type="number"
      className="h-6 w-12 rounded border border-transparent bg-transparent px-1 text-[11.5px] text-fg-muted focus:border-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-accent/20"
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Add chapter dialog                                                          */
/* -------------------------------------------------------------------------- */

function AddChapterDialog({
  open,
  onClose,
  board,
  classNum,
  subject,
  existingDisplayOrders,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  board: string;
  classNum: number;
  subject: string;
  existingDisplayOrders: number[];
  onCreated: () => void;
}) {
  // Default displayOrder = max(existing) + 1, so the new chapter sorts last.
  const nextOrder =
    existingDisplayOrders.length > 0 ? Math.max(...existingDisplayOrders) + 1 : 1;
  const [chapterTitle, setChapterTitle] = useState('');
  const [chapterNumber, setChapterNumber] = useState('');
  const [displayOrder, setDisplayOrder] = useState(String(nextOrder));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async () => {
    if (!chapterTitle.trim()) {
      setError('Title is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: CreateCurriculumChapterDto = {
        board,
        class: classNum,
        subject,
        chapterTitle: chapterTitle.trim(),
        chapterNumber: chapterNumber.trim() || undefined,
        displayOrder: Number(displayOrder) || undefined,
        topics: [],
      };
      await api.curriculum.createChapter(body);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-fg/35 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add chapter"
        className="relative w-full max-w-md overflow-hidden rounded-xl border border-border bg-bg-panel shadow-floating"
      >
        <div className="flex items-start justify-between border-b border-border-subtle px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-fg">Add chapter</h2>
            <p className="mt-0.5 text-xs text-fg-muted">
              {board} · Class {classNum} · {subject}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="grid h-7 w-7 place-items-center rounded-md text-fg-muted transition hover:bg-bg-subtle hover:text-fg"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <label className="block">
            <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
              Title <span className="text-red-500">*</span>
            </span>
            <input
              value={chapterTitle}
              onChange={(e) => setChapterTitle(e.target.value)}
              placeholder="e.g. Conservation of Plants and Animals"
              autoFocus
              className="h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
                Number
              </span>
              <input
                value={chapterNumber}
                onChange={(e) => setChapterNumber(e.target.value)}
                placeholder="5"
                className="h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
                Display order
              </span>
              <input
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value)}
                type="number"
                className="h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
            </label>
          </div>

          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">
              <AlertTriangle size={12} className="mr-1 inline" />
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border-subtle bg-bg-subtle/40 px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Creating…
              </>
            ) : (
              <>Create chapter</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Misc sub-pieces                                                             */
/* -------------------------------------------------------------------------- */

function SelectField({
  label,
  value,
  options,
  onChange,
  loading,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          className={cn(
            'h-10 w-full appearance-none rounded-md border border-border bg-white pl-3 pr-9 text-[13.5px] text-fg',
            'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20',
            'disabled:cursor-not-allowed disabled:bg-bg-subtle/40 disabled:text-fg-subtle',
          )}
        >
          <option value="">{placeholder ?? 'Select…'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {loading ? (
          <Loader2
            size={14}
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-fg-subtle"
          />
        ) : (
          <ChevronDown
            size={14}
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-subtle"
          />
        )}
      </div>
    </label>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="mt-10 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-bg-panel/50 py-16 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-xl bg-accent-soft text-accent">
        <Library size={24} />
      </span>
      <h3 className="mt-4 text-base font-semibold text-fg">{title}</h3>
      <p className="mt-1.5 max-w-md text-[13px] text-fg-muted">{detail}</p>
    </div>
  );
}
